import * as React from 'react';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Collapse,
    Divider, FormControl, Grid, IconButton, InputLabel, MenuItem,
    Paper, Select, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RepeatIcon from '@mui/icons-material/Repeat';
import { DataGrid } from '@mui/x-data-grid';
import ReactExport from 'react-export-excel-xlsx-fix';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import ApiClient from '../../../api/ApiClient';
import EndPoint from '../../../api/Endpoints';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { downloadDashboardPDF } from './DashboardPDF';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const DONATION_TYPES = [
    { value: 'all', label: 'Todos' },
    { value: 'recurrente', label: 'Recurrente' },
    { value: 'unica', label: 'Única' },
];

const PAYMENT_METHODS = [
    { value: 'all', label: 'Todos' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'qr', label: 'QR' },
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'completada', label: 'Completada' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'fallida', label: 'Fallida' },
];

const CITIES = [
    { value: 'all', label: 'Todas' },
    { value: 'La Paz', label: 'La Paz' },
    { value: 'Santa Cruz', label: 'Santa Cruz' },
    { value: 'Cochabamba', label: 'Cochabamba' },
    { value: 'Chuquisaca', label: 'Chuquisaca' },
    { value: 'Potosí', label: 'Potosí' },
    { value: 'Oruro', label: 'Oruro' },
    { value: 'Tarija', label: 'Tarija' },
    { value: 'Beni', label: 'Beni' },
    { value: 'Pando', label: 'Pando' },
];

const PRESET_RANGES = [
    { label: 'Hoy', value: 'today' },
    { label: 'Esta semana', value: 'this_week' },
    { label: 'Este mes', value: 'this_month' },
    { label: 'Mes anterior', value: 'last_month' },
    { label: 'Últimos 3 meses', value: 'last_3months' },
    { label: 'Este año', value: 'this_year' },
    { label: 'Personalizado', value: 'custom' },
];

const SAVED_FILTERS_KEY = 'unicef_dashboard_saved_filters';

const StatCard = styled(Card)(({ theme, color }) => ({
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${color || theme.palette.primary.main}`,
    height: '100%',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: 8,
}));

function getDateRange(preset) {
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    switch (preset) {
        case 'today':
            return { from: todayStr, to: todayStr };
        case 'this_week': {
            const day = now.getDay();
            const monday = new Date(now);
            monday.setDate(now.getDate() - ((day + 6) % 7));
            return { from: format(monday, 'yyyy-MM-dd'), to: todayStr };
        }
        case 'this_month':
            return { from: format(startOfMonth(now), 'yyyy-MM-dd'), to: todayStr };
        case 'last_month': {
            const lm = subMonths(now, 1);
            return { from: format(startOfMonth(lm), 'yyyy-MM-dd'), to: format(endOfMonth(lm), 'yyyy-MM-dd') };
        }
        case 'last_3months':
            return { from: format(subMonths(now, 3), 'yyyy-MM-dd'), to: todayStr };
        case 'this_year':
            return { from: format(new Date(now.getFullYear(), 0, 1), 'yyyy-MM-dd'), to: todayStr };
        default:
            return { from: format(subMonths(now, 1), 'yyyy-MM-dd'), to: todayStr };
    }
}

const defaultFilters = () => {
    const range = getDateRange('this_month');
    return {
        datePreset: 'this_month',
        dateFrom: range.from,
        dateFromTime: '00:00',
        dateTo: range.to,
        dateToTime: '23:59',
        donationType: 'all',
        paymentMethod: 'all',
        status: 'all',
        city: 'all',
        minAmount: '',
        maxAmount: '',
    };
};

const TABLE_COLUMNS = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'fecha', headerName: 'Fecha', width: 130 },
    { field: 'donante', headerName: 'Donante', width: 180, flex: 1 },
    { field: 'email', headerName: 'Email', width: 200, flex: 1 },
    { field: 'ciudad', headerName: 'Ciudad', width: 120 },
    { field: 'tipo', headerName: 'Tipo', width: 140,
        renderCell: (p) => <Chip size="small" label={p.value}
            color={p.value === 'Recurrente' ? 'primary' : p.value === 'Única' ? 'secondary' : 'default'} /> },
    { field: 'metodoPago', headerName: 'Método', width: 110 },
    { field: 'monto', headerName: 'Monto (Bs.)', width: 120, type: 'number',
        renderCell: (p) => `Bs. ${Number(p.value).toFixed(2)}` },
    { field: 'estado', headerName: 'Estado', width: 120,
        renderCell: (p) => <Chip size="small" label={p.value}
            color={p.value === 'Completada' ? 'success' : p.value === 'Pendiente' ? 'warning' : 'error'} /> },
];

// Simple inline bar chart using divs (no external chart lib needed)
function SimpleBarChart({ data, label, color }) {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => d.value), 1);
    return (
        <Box>
            {data.map((item, i) => (
                <Box key={i} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                        <Typography variant="caption" fontWeight={600}>{item.value}</Typography>
                    </Box>
                    <Box sx={{ height: 10, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{
                            height: '100%',
                            width: `${(item.value / max) * 100}%`,
                            bgcolor: color || 'primary.main',
                            borderRadius: 1,
                            transition: 'width 0.6s ease'
                        }} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// Monthly trend line using inline SVG
function TrendChart({ data }) {
    if (!data || data.length < 2) return null;
    const W = 100, H = 48;
    const maxVal = Math.max(...data.map(d => d.total), 1);
    const pts = data.map((d, i) => {
        const x = (i / (data.length - 1)) * W;
        const y = H - (d.total / maxVal) * (H - 4);
        return `${x},${y}`;
    }).join(' ');
    return (
        <Box sx={{ mt: 1 }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 60 }} preserveAspectRatio="none">
                <polyline points={pts} fill="none" stroke="#1976d2" strokeWidth="2" />
                {data.map((d, i) => {
                    const x = (i / (data.length - 1)) * W;
                    const y = H - (d.total / maxVal) * (H - 4);
                    return <circle key={i} cx={x} cy={y} r="2" fill="#1976d2" />;
                })}
            </svg>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {data.map((d, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                        {d.month}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

export default function DonationsDashboard() {
    const [filters, setFilters] = React.useState(defaultFilters);
    const [appliedFilters, setAppliedFilters] = React.useState(null);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [tableRows, setTableRows] = React.useState([]);
    const [tableTotal, setTableTotal] = React.useState(0);
    const [tablePage, setTablePage] = React.useState(0);
    const [tablePageSize, setTablePageSize] = React.useState(25);
    const [showFilters, setShowFilters] = React.useState(true);
    const [savedPresets, setSavedPresets] = React.useState([]);
    const [savePresetName, setSavePresetName] = React.useState('');
    const [showSaveInput, setShowSaveInput] = React.useState(false);
    const [showPresetsPanel, setShowPresetsPanel] = React.useState(false);

    // Load saved presets from localStorage
    React.useEffect(() => {
        try {
            const stored = localStorage.getItem(SAVED_FILTERS_KEY);
            if (stored) setSavedPresets(JSON.parse(stored));
        } catch (_) {}
    }, []);

    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'datePreset' && value !== 'custom') {
                const range = getDateRange(value);
                updated.dateFrom = range.from;
                updated.dateTo = range.to;
                updated.dateFromTime = '00:00';
                updated.dateToTime = '23:59';
            }
            return updated;
        });
    };

    const fetchDonations = async (activeFilters, page, pageSize) => {
        try {
            const resp = await ApiClient.doPost(EndPoint.getDashboardDonations, {
                ...activeFilters,
                page: page + 1,
                pageSize,
            });
            setTableRows(resp?.rows || []);
            setTableTotal(resp?.total || 0);
        } catch (_) {
            setTableRows([]);
            setTableTotal(0);
        }
    };

    const handleQuery = async () => {
        setLoading(true);
        const active = { ...filters };
        setAppliedFilters(active);
        setTablePage(0);
        try {
            const summary = await ApiClient.doPost(EndPoint.getDashboardSummary, active);
            setData(summary);
            setShowFilters(false);
            await fetchDonations(active, 0, tablePageSize);
        } catch (_) {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setTablePage(newPage);
        fetchDonations(appliedFilters, newPage, tablePageSize);
    };

    const handlePageSizeChange = (newPageSize) => {
        setTablePageSize(newPageSize);
        setTablePage(0);
        fetchDonations(appliedFilters, 0, newPageSize);
    };

    const handleReset = () => {
        setFilters(defaultFilters());
        setData(null);
        setAppliedFilters(null);
        setTableRows([]);
        setTableTotal(0);
        setTablePage(0);
        setShowFilters(true);
    };

    const savePreset = () => {
        if (!savePresetName.trim()) return;
        const preset = { name: savePresetName.trim(), filters: { ...filters } };
        const updated = [...savedPresets.filter(p => p.name !== preset.name), preset];
        setSavedPresets(updated);
        localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(updated));
        setSavePresetName('');
        setShowSaveInput(false);
    };

    const loadPreset = (preset) => {
        setFilters({ ...preset.filters });
        setShowPresetsPanel(false);
    };

    const deletePreset = (name) => {
        const updated = savedPresets.filter(p => p.name !== name);
        setSavedPresets(updated);
        localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(updated));
    };

    const handlePrint = () => window.print();

    const [pdfLoading, setPdfLoading] = React.useState(false);
    const handleDownloadPDF = async () => {
        setPdfLoading(true);
        try {
            await downloadDashboardPDF({ summary: data, rows: tableRows, appliedFilters });
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, mt: 8, maxWidth: 1400, mx: 'auto' }} className="dashboard-print-root">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Dashboard de Donaciones</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Consulta y analiza las donaciones del sistema
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {data && (
                        <>
                            <Tooltip title="Imprimir reporte">
                                <IconButton onClick={handlePrint} size="small"><PrintIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Descargar PDF">
                                <IconButton onClick={handleDownloadPDF} size="small" disabled={pdfLoading}>
                                    {pdfLoading ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
                                </IconButton>
                            </Tooltip>
                            <ExcelFile
                                filename={`donaciones_${appliedFilters?.dateFrom}_${appliedFilters?.dateTo}`}
                                element={
                                    <Tooltip title="Descargar Excel">
                                        <IconButton size="small"><DownloadIcon /></IconButton>
                                    </Tooltip>
                                }
                            >
                                <ExcelSheet data={tableRows} name="Donaciones">
                                    <ExcelColumn label="ID" value="id" />
                                    <ExcelColumn label="Fecha" value="fecha" />
                                    <ExcelColumn label="Donante" value="donante" />
                                    <ExcelColumn label="Email" value="email" />
                                    <ExcelColumn label="Ciudad" value="ciudad" />
                                    <ExcelColumn label="Tipo" value="tipo" />
                                    <ExcelColumn label="Método de Pago" value="metodoPago" />
                                    <ExcelColumn label="Monto (Bs.)" value="monto" />
                                    <ExcelColumn label="Estado" value="estado" />
                                </ExcelSheet>
                            </ExcelFile>
                            <Button
                                size="small"
                                startIcon={<FilterListIcon />}
                                variant="outlined"
                                onClick={() => setShowFilters(v => !v)}
                            >
                                {showFilters ? 'Ocultar filtros' : 'Editar filtros'}
                            </Button>
                        </>
                    )}
                    <Button
                        size="small"
                        variant={showPresetsPanel ? 'contained' : 'outlined'}
                        startIcon={savedPresets.length > 0 ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                        onClick={() => setShowPresetsPanel(v => !v)}
                        color="primary"
                    >
                        Filtros guardados{savedPresets.length > 0 ? ` (${savedPresets.length})` : ''}
                    </Button>
                </Stack>
            </Box>

            {/* Saved Presets Panel */}
            <Collapse in={showPresetsPanel}>
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'primary.light', bgcolor: '#f0f4ff' }} elevation={0}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <BookmarkIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                            Filtros guardados
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            — haz clic en uno para cargarlo
                        </Typography>
                    </Box>
                    {savedPresets.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No hay filtros guardados. Abre los filtros y usa <strong>Guardar filtros actuales</strong> para guardar una consulta.
                        </Typography>
                    ) : (
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {savedPresets.map(p => (
                                <Chip
                                    key={p.name}
                                    label={p.name}
                                    onClick={() => loadPreset(p)}
                                    onDelete={() => deletePreset(p.name)}
                                    color="primary"
                                    variant="filled"
                                    size="medium"
                                    icon={<BookmarkIcon />}
                                    sx={{ fontWeight: 600 }}
                                />
                            ))}
                        </Stack>
                    )}
                </Paper>
            </Collapse>

            {/* Filter Panel */}
            <Collapse in={showFilters}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }} elevation={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <SectionTitle><TuneIcon fontSize="small" /> Configurar consulta</SectionTitle>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {showSaveInput ? (
                                <>
                                    <TextField
                                        size="small"
                                        placeholder="Nombre del filtro..."
                                        value={savePresetName}
                                        onChange={e => setSavePresetName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && savePreset()}
                                        autoFocus
                                        sx={{ width: 200 }}
                                        InputProps={{ startAdornment: <BookmarkIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} /> }}
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={savePreset}
                                        disabled={!savePresetName.trim()}
                                    >
                                        Guardar
                                    </Button>
                                    <Button size="small" variant="outlined" color="inherit" onClick={() => { setShowSaveInput(false); setSavePresetName(''); }}>
                                        Cancelar
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={() => setShowSaveInput(true)}
                                    variant="contained"
                                    color="success"
                                >
                                    Guardar filtros actuales
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    <Grid container spacing={2}>
                        {/* Date Preset */}
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Rango de fechas</InputLabel>
                                <Select
                                    value={filters.datePreset}
                                    label="Rango de fechas"
                                    onChange={e => handleFilterChange('datePreset', e.target.value)}
                                >
                                    {PRESET_RANGES.map(r => (
                                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* From / To dates */}
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                fullWidth size="small" type="date" label="Desde"
                                InputLabelProps={{ shrink: true }}
                                value={filters.dateFrom}
                                onChange={e => handleFilterChange('dateFrom', e.target.value)}
                                disabled={filters.datePreset !== 'custom'}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                fullWidth size="small" type="date" label="Hasta"
                                InputLabelProps={{ shrink: true }}
                                value={filters.dateTo}
                                onChange={e => handleFilterChange('dateTo', e.target.value)}
                                disabled={filters.datePreset !== 'custom'}
                            />
                        </Grid>
                        {filters.datePreset === 'custom' && (
                            <>
                                <Grid item xs={6} sm={3} md={2}>
                                    <TextField
                                        fullWidth size="small" type="time" label="Hora desde"
                                        InputLabelProps={{ shrink: true }}
                                        value={filters.dateFromTime}
                                        onChange={e => handleFilterChange('dateFromTime', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3} md={2}>
                                    <TextField
                                        fullWidth size="small" type="time" label="Hora hasta"
                                        InputLabelProps={{ shrink: true }}
                                        value={filters.dateToTime}
                                        onChange={e => handleFilterChange('dateToTime', e.target.value)}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Donation type */}
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo de donación</InputLabel>
                                <Select
                                    value={filters.donationType}
                                    label="Tipo de donación"
                                    onChange={e => handleFilterChange('donationType', e.target.value)}
                                >
                                    {DONATION_TYPES.map(t => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Payment method */}
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Método de pago</InputLabel>
                                <Select
                                    value={filters.paymentMethod}
                                    label="Método de pago"
                                    onChange={e => handleFilterChange('paymentMethod', e.target.value)}
                                >
                                    {PAYMENT_METHODS.map(m => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Status */}
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    value={filters.status}
                                    label="Estado"
                                    onChange={e => handleFilterChange('status', e.target.value)}
                                >
                                    {STATUS_OPTIONS.map(s => (
                                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* City */}
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Ciudad</InputLabel>
                                <Select
                                    value={filters.city}
                                    label="Ciudad"
                                    onChange={e => handleFilterChange('city', e.target.value)}
                                >
                                    {CITIES.map(c => (
                                        <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Amount range */}
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                fullWidth size="small" type="number" label="Monto mín. (Bs.)"
                                value={filters.minAmount}
                                onChange={e => handleFilterChange('minAmount', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                fullWidth size="small" type="number" label="Monto máx. (Bs.)"
                                value={filters.maxAmount}
                                onChange={e => handleFilterChange('maxAmount', e.target.value)}
                            />
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
                                    Limpiar
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                                    onClick={handleQuery}
                                    disabled={loading}
                                    size="large"
                                >
                                    {loading ? 'Consultando...' : 'Consultar dashboard'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            {/* No data yet */}
            {!data && !loading && (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }} elevation={0} variant="outlined">
                    <BarChartIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight={600}>
                        Configura los filtros y presiona "Consultar dashboard"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Los datos no se cargarán automáticamente para evitar consultas innecesarias.
                    </Typography>
                </Paper>
            )}

            {/* Dashboard content */}
            {data && (
                <>
                    {/* Applied filters summary */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">Filtros activos:</Typography>
                        <Chip size="small" label={`${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`} />
                        {appliedFilters.donationType !== 'all' && <Chip size="small" label={DONATION_TYPES.find(t => t.value === appliedFilters.donationType)?.label} />}
                        {appliedFilters.paymentMethod !== 'all' && <Chip size="small" label={PAYMENT_METHODS.find(m => m.value === appliedFilters.paymentMethod)?.label} />}
                        {appliedFilters.status !== 'all' && <Chip size="small" label={STATUS_OPTIONS.find(s => s.value === appliedFilters.status)?.label} />}
                        {appliedFilters.city !== 'all' && <Chip size="small" label={appliedFilters.city} />}
                    </Box>

                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} md={3}>
                            <StatCard color="#1976d2">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Total recaudado</Typography>
                                            <Typography variant="h5" fontWeight={700}>Bs. {data.totalAmount.toLocaleString('es-BO')}</Typography>
                                            <Typography variant="caption" color="success.main">+{data.amountGrowth}% vs periodo anterior</Typography>
                                        </Box>
                                        <AttachMoneyIcon sx={{ color: '#1976d2', fontSize: 36, opacity: 0.6 }} />
                                    </Box>
                                </CardContent>
                            </StatCard>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <StatCard color="#2e7d32">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Total donaciones</Typography>
                                            <Typography variant="h5" fontWeight={700}>{data.totalDonations.toLocaleString()}</Typography>
                                            <Typography variant="caption" color="success.main">+{data.donationsGrowth}% vs periodo anterior</Typography>
                                        </Box>
                                        <TrendingUpIcon sx={{ color: '#2e7d32', fontSize: 36, opacity: 0.6 }} />
                                    </Box>
                                </CardContent>
                            </StatCard>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <StatCard color="#ed6c02">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Donantes únicos</Typography>
                                            <Typography variant="h5" fontWeight={700}>{data.uniqueDonors.toLocaleString()}</Typography>
                                            <Typography variant="caption" color="text.secondary">promedio Bs. {data.avgDonation}</Typography>
                                        </Box>
                                        <PeopleIcon sx={{ color: '#ed6c02', fontSize: 36, opacity: 0.6 }} />
                                    </Box>
                                </CardContent>
                            </StatCard>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <StatCard color="#9c27b0">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Donaciones recurrentes</Typography>
                                            <Typography variant="h5" fontWeight={700}>{data.recurringDonations.toLocaleString()}</Typography>
                                            <Typography variant="caption" color="text.secondary">{data.recurringPct}% del total</Typography>
                                        </Box>
                                        <RepeatIcon sx={{ color: '#9c27b0', fontSize: 36, opacity: 0.6 }} />
                                    </Box>
                                </CardContent>
                            </StatCard>
                        </Grid>
                    </Grid>

                    {/* Charts row */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Trend */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                                <SectionTitle><TrendingUpIcon fontSize="small" /> Tendencia mensual (Bs.)</SectionTitle>
                                <TrendChart data={data.monthlyTrend} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    {data.monthlyTrend.map((d, i) => (
                                        <Box key={i} sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={600}>
                                                {d.total >= 1000 ? `${(d.total / 1000).toFixed(1)}k` : d.total}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* By type */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                                <SectionTitle>Por tipo de donación</SectionTitle>
                                <SimpleBarChart data={data.byType} color="#1976d2" />
                            </Paper>
                        </Grid>

                        {/* By payment method */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                                <SectionTitle>Por método de pago</SectionTitle>
                                <SimpleBarChart data={data.byPaymentMethod} color="#ed6c02" />
                            </Paper>
                        </Grid>

                        {/* By city */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                                <SectionTitle>Por ciudad</SectionTitle>
                                <SimpleBarChart data={data.byCity} color="#2e7d32" />
                            </Paper>
                        </Grid>

                        {/* By status */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                                <SectionTitle>Por estado</SectionTitle>
                                <SimpleBarChart data={data.byStatus} color="#9c27b0" />
                            </Paper>
                        </Grid>

                        {/* Top campaigns */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                                <SectionTitle>Top campañas por monto (Bs.)</SectionTitle>
                                <SimpleBarChart data={data.byCampaign} color="#0288d1" />
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Table */}
                    <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <SectionTitle sx={{ mb: 0 }}>Detalle de donaciones ({tableTotal})</SectionTitle>
                        </Box>
                        <Box sx={{ height: 420 }}>
                            <DataGrid
                                rows={tableRows}
                                columns={TABLE_COLUMNS}
                                rowCount={tableTotal}
                                page={tablePage}
                                pageSize={tablePageSize}
                                rowsPerPageOptions={[10, 25, 50]}
                                paginationMode="server"
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                disableSelectionOnClick
                                density="compact"
                                sx={{ border: 'none', '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50' } }}
                            />
                        </Box>
                    </Paper>
                </>
            )}

            <style>{`
                @media print {
                    .dashboard-print-root { margin: 0 !important; padding: 8px !important; }
                    button, .MuiIconButton-root { display: none !important; }
                }
            `}</style>
        </Box>
    );
}
